import { useTranslation } from 'react-i18next';
import { useReviews } from '@/lib/reviewsStore';

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

function getInitialsColor(name: string): string {
  const palette = [
    'bg-amber-100 text-amber-700',
    'bg-emerald-100 text-emerald-700',
    'bg-rose-100 text-rose-700',
    'bg-teal-100 text-teal-700',
    'bg-orange-100 text-orange-700',
    'bg-cyan-100 text-cyan-700',
    'bg-violet-100 text-violet-700',
    'bg-lime-100 text-lime-700',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return palette[Math.abs(hash) % palette.length];
}

export default function TestimonialsSection() {
  const { t } = useTranslation();
  const allReviews = useReviews();
  const visible = allReviews.filter((r) => r.visible);
  // Show first 2 visible reviews on home
  const featured = visible.slice(0, 2);

  return (
    <section id="testimonials" className="py-20 md:py-28 px-6 md:px-10 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14 md:mb-18">
          <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-accent-600 font-label">
            <i className="ri-vip-diamond-line text-sm"></i>
            {t('testimonials.tag')}
          </span>
          <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-900 mt-3 leading-tight">
            {t('testimonials.heading')}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 max-w-3xl mx-auto">
          {featured.map((review) => (
            <TestimonialCard key={review.id} review={review} />
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialCard({ review }: { review: import('@/lib/reviewsStore').Review }) {
  const initials = getInitials(review.name);
  const initialsColor = getInitialsColor(review.name);

  return (
    <div className="rounded-2xl bg-secondary-50 border border-secondary-100/60 p-4 md:p-7 transition-all duration-300 hover:border-secondary-200">
      <i className="ri-double-quotes-l text-xl md:text-2xl text-secondary-300"></i>
      <p className="mt-2 md:mt-3 text-sm text-foreground-700 leading-relaxed font-body">
        {review.text}
      </p>
      <div className="mt-3 md:mt-4 flex items-center gap-3">
        <div className={`w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${initialsColor}`}>
          <span className="font-heading text-xs md:text-sm font-bold">
            {initials}
          </span>
        </div>
        <div>
          <p className="text-xs md:text-sm font-label font-semibold text-foreground-800">{review.name}</p>
          <p className="text-[10px] md:text-xs text-foreground-400 font-body">{review.treatment}, {review.location}</p>
        </div>
      </div>
    </div>
  );
}